import type { ActiveContextState } from "../contexts/ActiveContextContext";
import apiService from "./apiService";

/**
 * SSO pro Virtus WhatsApp (CRM). Ao contrário do Leadflow, o CRM usa um token
 * dedicado de curta duração (endpoint /api/auth/sso-token, assinado com
 * VIRTUS_SSO_SHARED_SECRET) em vez do access_token normal do SMM.
 *
 * Usa `apiService` (em vez de `fetch` direto) para resolver a URL do backend
 * corretamente — um `fetch("/api/...")` cru resolve contra a origem da
 * própria página (o front, ex.: smm.virtusautomation.com), não contra a API
 * (api-smm.virtusautomation.com), causando 503 em produção. `apiService` já
 * usa a mesma `API_BASE_URL` do resto do app, e anexa Authorization e
 * X-Active-Context automaticamente a partir do contexto ativo global.
 */
/**
 * Endereço do Virtus Chat (CRM). `VITE_VIRTUS_CHAT_URL` é o nome atual; `VITE_VIRTUS_CRM_URL_TEST`
 * (nome antigo) continua aceito para não quebrar os .env já existentes.
 */
export function getVirtusChatBaseUrl(): string {
  return (
    (import.meta.env.VITE_VIRTUS_CHAT_URL as string | undefined) ||
    (import.meta.env.VITE_VIRTUS_CRM_URL_TEST as string | undefined) ||
    "https://wpp.crm.virtusautomation.com"
  );
}

/** URL de entrada com SSO no CRM (token dedicado de curta duração). Usada também pela rota /launch. */
export async function getVirtusCrmSsoUrl(
  activeContext: ActiveContextState | null | undefined,
): Promise<string> {
  const crmBaseUrl = getVirtusChatBaseUrl();

  const data = await apiService.post<{ sso_token: string }>(
    "/api/auth/sso-token",
    {},
  );

  // Monta a URL de SSO do CRM: <base_crm>/sso?token=...&redirect=/settings?tab=whatsapp
  const crmOrigin = new URL(
    crmBaseUrl.includes("://") ? crmBaseUrl : `http://${crmBaseUrl}`,
  ).origin;
  const settingsUrl =
    import.meta.env.VITE_VIRTUS_CRM_SETTINGS_URL || `${crmOrigin}/dashboard`;
  const redirectPath = settingsUrl.replace(crmOrigin, "") || "/dashboard";
  let ssoUrl = `${crmOrigin}/sso?token=${encodeURIComponent(data.sso_token)}&redirect=${encodeURIComponent(redirectPath)}`;

  if (
    activeContext?.type === "CLIENT_COMPANY" &&
    activeContext?.clientCompany?.id
  ) {
    ssoUrl += `&client=${activeContext.clientCompany.id}`;
  }
  if (activeContext?.organization?.id) {
    ssoUrl += `&org=${activeContext.organization.id}`;
  }

  return ssoUrl;
}

export async function openVirtusCrmSSO(
  activeContext: ActiveContextState | null | undefined,
): Promise<void> {
  const ssoUrl = await getVirtusCrmSsoUrl(activeContext);
  window.open(ssoUrl, "_blank", "noopener,noreferrer");
}
